import useTranslation from '@app/hooks/use-translation';
import { AllStatusProject } from '@app/interfaces/analytics';
import {
  MetricTypesArrType,
  MetricObjType,
  MetricListArrType,
} from '@app/interfaces/pages-types/anatylics-metric.type';
import { AliasSettingsObj } from '@app/interfaces/settings.type';
import { useCreateChatMetaMetricMultiMutation } from '@app/store/api/chat-api/chat-metric.api';
import { useCreateMetaMetricMultiMutation } from '@app/store/api/metrics.api';
import { formBaseButtonBlockStyles } from '@app/styles/base-styles';
import { Button, Checkbox, Input, Toggle, TooltipWrapper } from '@ui';
import classNames from 'classnames';
import React, { FC, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
type MetaMetricCreateTablePropsType = {
  metric: MetricTypesArrType;
  editModal?: boolean;
  copyModal?: boolean;
  modalsSetState: (type: string) => void;
  status?: AllStatusProject | undefined;
  unitVal?: string;
  metricSettings?: MetricObjType | undefined;
  currentTemplate: 'chat' | 'voice';
  aliases: AliasSettingsObj[];
  metricList?: MetricListArrType[];
};

// const obj: MetricObjType = {
//       type: metric.type,
//       result_value_type: getResultValueType(watch('alias_id')),
//       export: {
//         export_result: data.export_result,
//         export_id: data.export_result ? data.export_id : undefined,
//       },
//       settings: {
//         alias_id: data.alias_id,
//       },

type FormType = {
  name: string;
  settings: { alias_id: string };
  dnt_display?: boolean;
  description: string;
  type: string;
  result_value_type: string;
  export: {
    export_result?: boolean;
    export_id?: string;
  };
};
const MetaMetricCreateTable: FC<MetaMetricCreateTablePropsType> = (props) => {
  const { aliases, modalsSetState, metric, currentTemplate, metricList } = props;
  const { id } = useParams();

  const createdMetaMetrics = useMemo(() => {
    if (!metricList) return [];
    return metricList.filter((m) => m.type === metric.type).map((m) => m.settings.alias_id);
  }, [metric.type, metricList]);

  // api
  const [createVoice] = useCreateMetaMetricMultiMutation();
  const [createChat] = useCreateChatMetaMetricMultiMutation();
  const create = currentTemplate === 'chat' ? createChat : createVoice;

  const { t } = useTranslation('pages.metricTemplates');
  const { t: tf } = useTranslation('common.forms');
  const closeModal = () => {
    modalsSetState(metric.type);
  };

  const {
    handleSubmit,
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<{ aliases: FormType[] }>();
  const { append, fields, remove } = useFieldArray({ control, name: 'aliases' });

  const getResultValueType = (id: string) => {
    return aliases?.find((alias) => alias.alias_id === id)?.type_value;
  };

  function onAddAlias(alias_id: string, name: string) {
    const findAliasInCheckedIndex = fields.findIndex(
      (field) => field.settings.alias_id === alias_id,
    );
    if (findAliasInCheckedIndex === -1) {
      append({
        name,
        dnt_display: false,
        settings: { alias_id },
        export: { export_result: false },
        description: ' ',
        type: metric.type,
        result_value_type: getResultValueType(alias_id) || 'str',
      });
    } else {
      remove(findAliasInCheckedIndex);
    }
  }

  function onSubmit(data: { aliases: FormType[] }) {
    create({ params: { project_id: id || '' }, body: data.aliases })
      .unwrap()
      .finally(() => closeModal());
  }
  const gridContainerClass = classNames(
    'grid grid-cols-[20px_220px_220px_215px_170px_200px] gap-x-[15px] items-baseline',
  );
  const fieldsNames = [
    ' ',
    t('alias'),
    t('metric_name'),
    t('dnt_display_in_record'),
    t('export_result'),
    t('export_id'),
  ];
  return (
    <div className="relative">
      <div className={`${gridContainerClass} pb-[20px]`}>
        {fieldsNames.map((name, index) => (
          <div className="text-[13px] text-0color font-[500]" key={name + index}>
            {name}
          </div>
        ))}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-y-[10px]">
          {aliases
            ?.filter((e) => e.type_meta !== 'system')
            .map((alias) => {
              const aliasIndexInFields = fields.findIndex(
                (field) => field.settings.alias_id === alias.alias_id,
              );
              return (
                <div key={alias.alias_id} className={gridContainerClass}>
                  <div>
                    <Checkbox
                      disabled={createdMetaMetrics.includes(alias.alias_id)}
                      touched={createdMetaMetrics.includes(alias.alias_id)}
                      checked={aliasIndexInFields !== -1}
                      onChange={() => onAddAlias(alias.alias_id, alias.name)}
                    />
                  </div>
                  <div>
                    <TooltipWrapper truncate id={alias.alias_id} content={alias.name}>
                      {alias.name}
                    </TooltipWrapper>
                  </div>
                  <div>
                    {aliasIndexInFields !== -1 && (
                      <Input {...register(`aliases.${aliasIndexInFields}.name`)} />
                    )}
                  </div>
                  <div className="justify-self-center">
                    <Toggle
                      checked={watch(`aliases.${aliasIndexInFields}.dnt_display`)}
                      onChecked={(val) =>
                        setValue(`aliases.${aliasIndexInFields}.dnt_display`, val)
                      }
                      disabled={aliasIndexInFields === -1}
                    />
                  </div>
                  <div className="justify-self-center">
                    <Toggle
                      checked={watch(`aliases.${aliasIndexInFields}.export.export_result`)}
                      onChecked={(val) =>
                        setValue(`aliases.${aliasIndexInFields}.export.export_result`, val)
                      }
                      disabled={aliasIndexInFields === -1}
                    />
                  </div>
                  <div>
                    <Input
                      {...register(`aliases.${aliasIndexInFields}.export.export_id`, {
                        required: {
                          value: !!watch(`aliases.${aliasIndexInFields}.export.export_result`),
                          message: tf('required'),
                        },
                      })}
                      error={errors?.aliases?.[aliasIndexInFields]?.export?.export_id?.message}
                      disabled={!watch(`aliases.${aliasIndexInFields}.export.export_result`)}
                    />
                  </div>
                </div>
              );
            })}
        </div>
        <div className={`${formBaseButtonBlockStyles} pb-[10px]`}>
          <Button type="submit" disabled={!fields.length} label={t('save')} />
          <Button onClick={() => closeModal()} fill="linked" label={t('cancel')} />
        </div>
      </form>
    </div>
  );
};

export default MetaMetricCreateTable;
